import { useEffect, useRef, useState } from 'react'
import { Button } from '../Common/Button'

type ClipboardInterceptorProps = {
  enabled: boolean
  onExtractBlueprint: (clipboardText: string) => Promise<void>
}

const SCHEDULE_PATTERN = /\b(schedule|deadline|timeline|todo|task|tasks|milestone|call sheet|arrival|setup|deliverable)\b/i

export const ClipboardInterceptor = ({ enabled, onExtractBlueprint }: ClipboardInterceptorProps) => {
  const [detectedText, setDetectedText] = useState<string | null>(null)
  const [isExtracting, setIsExtracting] = useState(false)
  const lastPromptedTextRef = useRef<string>('')

  useEffect(() => {
    if (!enabled || !navigator.clipboard?.readText) return

    const handleWorkspaceRefocusSync = () => {
      void navigator.clipboard.readText()
        .then((text) => {
          const normalizedText = text.trim()
          if (
            normalizedText.length <= 30 ||
            !SCHEDULE_PATTERN.test(normalizedText) ||
            normalizedText === lastPromptedTextRef.current
          ) return

          lastPromptedTextRef.current = normalizedText
          setDetectedText(normalizedText)
        })
        .catch(() => {
          // Clipboard reads require browser permission and may be denied on refocus.
        })
    }

    window.addEventListener('focus', handleWorkspaceRefocusSync)
    handleWorkspaceRefocusSync()
    return () => window.removeEventListener('focus', handleWorkspaceRefocusSync)
  }, [enabled])

  if (!enabled || !detectedText) return null

  return (
    <div
      className="clipboardInterceptorToast"
      role="status"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div>
        <strong>Intelligent Parsing</strong>
        <p>Unstructured workflow notes detected in your clipboard. Run Gemini AI extraction to generate canvas node branches instantly?</p>
      </div>
      <Button
        variant="tertiary"
        size="sm"
        disabled={isExtracting}
        onClick={() => {
          setDetectedText(null)
        }}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        size="sm"
        disabled={isExtracting}
        loading={isExtracting}
        onClick={() => {
          setIsExtracting(true)
          void onExtractBlueprint(detectedText)
            .then(() => setDetectedText(null))
            .finally(() => setIsExtracting(false))
        }}
      >
        {isExtracting ? 'Extracting' : 'Extract Blueprint'}
      </Button>
    </div>
  )
}
